import React from 'react'

import {
  Box,
  Button,
  CollapsableCard,
  Icon,
  List,
  RadioButton,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  PerformanceProcessLaunchDocument,
  PerformanceProcessLaunchModalFragment,
  PerformanceProcessScheduleDocument,
  PerformanceReviewOverviewHeaderDocument,
  PerformanceStartReviewProcessValidatorFailedCheckEnum,
  UpdateDeadlinePerformanceReviewProcessDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DeadlinePicker } from 'modules/performance_process/components/review/forms/Deadline'
import {
  scoreConfigurationToEvent,
  targetStrategyToEvent,
} from 'modules/performance_process/lib/events'

export const LaunchModal: React.FC<{
  review: PerformanceProcessLaunchModalFragment
  onClose?: () => void
}> = ({ review, onClose }) => {
  const [updateReviewDeadline, { loading: updateDeadlineLoading }] = useMutation(
    UpdateDeadlinePerformanceReviewProcessDocument,
    {
      extractorFn: (data) => data.performanceMutations.updateDeadlinePerformanceReviewProcess,
    }
  )
  const [startReviewMutation, { loading: startLoading }] = useMutation(
    PerformanceProcessLaunchDocument,
    {
      extractorFn: (data) => data.performanceMutations.startPerformanceReviewProcess,
      refetchQueries: [PerformanceReviewOverviewHeaderDocument],
      awaitRefetchQueries: true,
    }
  )
  const [scheduleReviewMutation, { loading: scheduleLoading }] = useMutation(
    PerformanceProcessScheduleDocument,
    {
      extractorFn: (data) => data.performanceMutations.schedulePerformanceReviewProcess,
      refetchQueries: [PerformanceReviewOverviewHeaderDocument],
      awaitRefetchQueries: true,
    }
  )
  const [isSchedulingEnabled, setIsSchedulingEnabled] = React.useState<boolean>(false)
  const [scheduledAt, setScheduledAt] = React.useState<Date | null>(null)
  const [deadline, setDeadline] = React.useState<Date | null>(
    review.endsAt ? new Date(review.endsAt) : null
  )

  const trackEvent = () => {
    track(trackingEvents.performanceProcess.launchReview, {
      reviewer_strategies: review.reviewerStrategies,
      reviewees: targetStrategyToEvent(review.targetStrategy),
      performance_score: scoreConfigurationToEvent(review.scoreConfiguration),
      end_date: i18nDateToString(new Date(), 'backend'),
    })
  }

  const handleUpdateDeadline = async (date: Date) => {
    if (!date) return
    if (date.toISOString() === review.endsAt?.toISOString?.()) return

    const response = await updateReviewDeadline({
      variables: {
        id: review.id,
        endsAt: date.toISOString(),
      },
    })

    const result = handleResult(response, (errors) => {
      if (!errors.messages) return
      app.setFlash({
        type: 'wrong',
        message: errors.messages ?? i18n.t('common.error'),
      })
    })

    if (!result.success) return false
    setDeadline(date)
    return result.success
  }

  const startReview = async () => {
    const response = await startReviewMutation({
      variables: {
        id: Number(review.id),
      },
    })

    const result = handleResult(response, () => null)

    if (result.success) {
      trackEvent()
      onClose?.()
    }

    return result
  }

  const scheduleReview = async () => {
    const response = await scheduleReviewMutation({
      variables: {
        id: Number(review.id),
        startsAt: scheduledAt?.toISOString(),
      },
    })

    const result = handleResult(response, (errors) => {
      if (!errors.messages) return
      app.setFlash({
        type: 'wrong',
        message: errors.messages ?? i18n.t('common.error'),
      })
    })

    if (result.success) {
      trackEvent()
      onClose?.()
    }

    return result
  }

  const hasErrors = review.startValidationErrors.length > 0
  const hasSchedulingTime = !(isSchedulingEnabled && !scheduledAt)
  const canLaunch = hasSchedulingTime && !hasErrors
  const isLoading = startLoading || scheduleLoading || updateDeadlineLoading

  const errorTranslations: Record<PerformanceStartReviewProcessValidatorFailedCheckEnum, string> = {
    ['invalid_deadline']: i18n.t(
      'performance_process.reviews.launch.validation_errors.invalid_deadline'
    ),
    ['missing_deadline']: i18n.t(
      'performance_process.reviews.launch.validation_errors.missing_deadline'
    ),
    ['missing_questions']: i18n.t(
      'performance_process.reviews.launch.validation_errors.missing_some_questions'
    ),
    ['missing_reviewer_strategy']: i18n.t(
      'performance_process.reviews.launch.validation_errors.missing_review_types'
    ),
    ['missing_target_strategy']: i18n.t(
      'performance_process.reviews.launch.validation_errors.missing_target_group'
    ),
    ['missing_target_strategy_members']: i18n.t(
      'performance_process.reviews.launch.validation_errors.missing_target_strategy_members'
    ),
    ['missing_title']: i18n.t('performance_process.reviews.launch.validation_errors.missing_title'),
    ['missing_potential_reviewers']: i18n.t(
      'performance_process.reviews.launch.validation_errors.missing_potential_reviewers'
    ),
    ['invalid_section_weights_sum']: i18n.t(
      'performance_process.reviews.launch.validation_errors.invalid_section_weights_sum'
    ),
  }

  return (
    <Modal
      title={i18n.t('performance_process.reviews.launch_review_modal.title')}
      description={i18n.t('performance_process.reviews.launch_review_modal.description')}
      illustration="performance"
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose} disabled={isLoading}>
            {i18n.t('common.cancel')}
          </Button>

          {isSchedulingEnabled ? (
            <Button disabled={!canLaunch || isLoading} onClick={() => scheduleReview()}>
              {i18n.t('performance_process.reviews.schedule_review')}
            </Button>
          ) : (
            <Button disabled={!canLaunch || isLoading} onClick={() => startReview()}>
              {i18n.t('performance_process.reviews.launch_review_button')}
            </Button>
          )}
        </>
      }
    >
      <Box gap="s16">
        <CollapsableCard
          open={false}
          Content={<></>}
          Card={
            <Box>
              <Box flexDirection="row" justifyContent="spaceBetween">
                <Text.Normal weight="medium">
                  {i18n.t('performance_process.reviews.launch_review_modal.launch_now')}
                </Text.Normal>

                <RadioButton
                  size="medium"
                  value={i18n.t('performance_process.reviews.launch_review_modal.launch_now')}
                  onChange={() => setIsSchedulingEnabled(false)}
                  checked={!isSchedulingEnabled}
                />
              </Box>
            </Box>
          }
        />
        <CollapsableCard
          open={isSchedulingEnabled}
          Card={
            <Box>
              <Box flexDirection="row" justifyContent="spaceBetween">
                <Text.Normal weight="medium">
                  {i18n.t('performance_process.reviews.launch_review_modal.schedule')}
                </Text.Normal>

                <RadioButton
                  size="medium"
                  value={i18n.t('performance_process.reviews.launch_review_modal.schedule')}
                  onChange={() => setIsSchedulingEnabled(true)}
                  checked={isSchedulingEnabled}
                />
              </Box>
            </Box>
          }
          Content={
            <Box gap="s12">
              <Text.Normal weight="semibold">
                {i18n.t('performance_process.reviews.launch_review_modal.kickoff.title')}
              </Text.Normal>
              <DeadlinePicker
                date={scheduledAt}
                maxDate={deadline || undefined}
                readonly={isLoading}
                onChange={(date) => setScheduledAt(date)}
              />
              <Text.Normal>
                {i18n.t('performance_process.reviews.launch_review_modal.kickoff.description')}
              </Text.Normal>
            </Box>
          }
        />

        <Box gap="s16">
          <Text size="400" color="textPrimary" weight="medium">
            {i18n.t('performance_process.reviews.launch_review_modal.deadline.title')}
          </Text>
          <Text.Normal>
            {i18n.t('performance_process.reviews.launch_review_modal.deadline.description')}
          </Text.Normal>
          <Box flexDirection="row" gap="s16">
            <DeadlinePicker
              minDate={scheduledAt || undefined}
              readonly={isLoading}
              date={deadline}
              onChange={handleUpdateDeadline}
            />
          </Box>
        </Box>

        <Box minWidth="s600">
          {review.startValidationErrors.length > 0 && (
            <List>
              {review.startValidationErrors.map((error, index) => (
                <List.Item key={index}>
                  <Box gap="s16" width="full" flexDirection="row" alignItems="center">
                    <Box>
                      <Icon.Medium color="radicalRed1000" icon="cross" />
                    </Box>
                    <Box>{errorTranslations[error]}</Box>
                  </Box>
                </List.Item>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </Modal>
  )
}
